import type { Refl_Type } from 'innovatint_base/common/color_calc/color_conv';
import { omitNil } from 'js/mylib/Utils';
import type {
  ApiThunk,
  ChoosePrimerParameters,
  ColourDetails,
  ColourRest,
  ColourSearchOptions,
  ColourSummary,
  CompatibleLocalFormula,
  FormulaCanInput,
  FormulaChange,
  FormulaChangesParameters,
  FormulaCorrectionResponse,
  FormulaOrderItem,
  FormulaVersion,
  LoadFormulaPayload,
  LocalFormula,
  MatchFormula,
  MatchingResponse,
  Nullable,
  PrimerColour,
  ProductWithFormula,
  SearchClosestResponse,
  SynthesizePropertiesResponse,
  SynthesizeProperty,
  Triplet,
} from './types/Formula';
import { authenticated, authenticated_cached } from './WebRequest';

export const CHOOSE_PRIMER_LOOK_UP_ONLY = 1;
export const CHOOSE_PRIMER_CALCULATE_ONLY = 2;
export const CHOOSE_PRIMER_LOOK_UP_AND_CALCULATE = 3;

export type {
  ApiThunk,
  ChoosePrimerParameters,
  ColourDetails,
  ColourRest,
  ColourSearchParams,
  ColourSummary,
  CompatibleLocalFormula,
  ComputedColour,
  CorrectedFormula,
  DeltaValues,
  FormulaCanInput,
  FormulaChange,
  FormulaChangesParameters,
  FormulaColorant,
  FormulaCorrectionData,
  FormulaCorrectionResponse,
  FormulaCorrectionType,
  FormulaOrderItem,
  FormulaOrderItemPrice,
  FormulaVersion,
  LoadFormulaPayload,
  LocalFormula,
  MatchFormula,
  MatchingResponse,
  MatchResult,
  Nullable,
  PrimerColour,
  PrimerMode,
  ProductWithFormula,
  SearchClosestResponse,
  SynthesizePropertiesResponse,
  SynthesizeProperty,
  Triplet,
} from './types/Formula';

export default class FormulaAPI {
  static colorByCode(colourcode: string): ApiThunk<ColourRest[]> {
    return authenticated.get<ColourRest[]>('/rest/colour/', {
      params: { colourcode, '.limit': 1 },
    });
  }

  static searchColors(
    zoneid: number,
    colourcardid?: Nullable<number>,
    search?: Nullable<string>,
    options?: ColourSearchOptions
  ): ApiThunk<ColourSummary[]> {
    // NOTE: must use omitNil with cached queries
    const params = omitNil({
      search,
      colourcardid,
      ...options,
    });
    return authenticated_cached.get<ColourSummary[]>(
      '/query/zone/' + zoneid + '/colours/',
      {
        params,
      }
    );
  }
  static searchColorsForProduct(
    systemgroupid: number,
    zoneid: number,
    productid?: Nullable<number>,
    colourcardid?: Nullable<number>,
    search?: Nullable<string>,
    options?: ColourSearchOptions
  ): ApiThunk<ColourSummary[]> {
    const params = omitNil({
      search,
      colourcardid,
      ...options,
    });
    return authenticated_cached.get<ColourSummary[]>(
      `/query/systemgroup/${systemgroupid}/zone/${zoneid}/colours_for_product/` +
        (productid || ''),
      { params }
    );
  }
  static cardsForProduct(
    systemgroupid: number,
    zoneid: number,
    productid: number
  ): ApiThunk<number[]> {
    return authenticated_cached.get<number[]>(
      `/query/systemgroup/${systemgroupid}/zone/${zoneid}/cards_for_product/` +
        productid
    );
  }
  static searchColorsForMatching(
    systemgroupid: number,
    zoneid: number,
    productid: number,
    colourcardid?: Nullable<number>,
    search?: Nullable<string>,
    options?: ColourSearchOptions
  ): ApiThunk<ColourSummary[]> {
    const params = omitNil({
      search,
      colourcardid,
      ...options,
    });
    return authenticated_cached.get<ColourSummary[]>(
      `/query/systemgroup/${systemgroupid}/zone/${zoneid}/colours_for_matching/` +
        productid,
      { params }
    );
  }
  static searchMeasuredColors(
    zoneid: number,
    colourcardid?: Nullable<number>,
    search?: Nullable<string>,
    options?: ColourSearchOptions
  ): ApiThunk<ColourSummary[]> {
    const params = omitNil({
      search,
      colourcardid,
      ...options,
    });
    return authenticated_cached.get<ColourSummary[]>(
      '/query/zone/' + zoneid + '/measured_colours/',
      { params }
    );
  }
  static searchProducts(
    systemgroupid: number,
    zoneid: number,
    colourid?: Nullable<number>
  ): ApiThunk<ProductWithFormula[]> {
    // NOTE: must use omitNil with cached queries
    //'/query/zone/<int:zoneid>/products_for_colour/<int:colourid>'
    return authenticated_cached.get<ProductWithFormula[]>(
      `/query/systemgroup/${systemgroupid}/zone/${zoneid}/products_for_colour/` +
        (colourid || '')
    );
  }

  static loadFormula(
    systemgroupid: number,
    payload: LoadFormulaPayload
  ): ApiThunk<FormulaVersion[]> {
    return authenticated.get<FormulaVersion[]>(
      `/query/systemgroup/${systemgroupid}/formula_versions/${payload.colourid}/${payload.productid}`
    );
  }

  static formulaAsOrderitem(
    systemgroupid: number,
    zoneid: number,
    colourid: number,
    productid: number,
    can: FormulaCanInput,
    siteid: Nullable<number> = null,
    allow_cansize_change: Nullable<boolean> = null,
    unique_color_codes = true
  ): ApiThunk<FormulaOrderItem> {
    const {
      cansizeid,
      nominalamount,
      gravimetric: gravimetricnominal,
      cansizecode,
    } = can;
    const params = omitNil({
      siteid,
      allow_cansize_change,
      nominalamount,
      gravimetricnominal,
      cansizecode,
      unique_color_codes,
    });
    const query =
      `/query/systemgroup/${systemgroupid}/zone/${zoneid}/formula_as_orderitem/` +
      `${colourid}/${productid}` +
      (cansizeid != null ? `/${cansizeid}` : '');

    return authenticated.get<FormulaOrderItem>(query, { params });
  }

  static matchingSearchClosest(
    zoneid: number,
    siteid: Nullable<number>,
    refl: Nullable<Refl_Type>,
    lab: Triplet,
    productid: number,
    cansizeid: Nullable<number>,
    nominalamount: Nullable<number>,
    gravimetric: Nullable<boolean>,
    colourid: Nullable<number>,
    coldataclass: Nullable<string>,
    max_de: Nullable<number>,
    deoption: Nullable<number>,
    maxresults: Nullable<number>,
    colourcardid: Nullable<number>,
    systemgroupid: number,
    closest_color_preferred_cards_only: Nullable<boolean>
  ): ApiThunk<SearchClosestResponse> {
    return authenticated.post<SearchClosestResponse>(
      `/matching/systemgroup/${systemgroupid}/zone/${zoneid}/search_closest/`,
      {
        siteid,
        refl,
        lab,
        productid,
        cansizeid,
        nominalamount,
        gravimetric,
        colourid,
        coldataclass,
        max_de,
        deoption,
        maxresults,
        colourcardid,
        closest_color_preferred_cards_only,
      }
    );
  }

  static matchingMatch(
    zoneid: number,
    systemgroupid: number,
    siteid: Nullable<number>,
    refl: Refl_Type,
    productid: number,
    cansizeid: Nullable<number>,
    nominalamount: Nullable<number>,
    gravimetric: Nullable<boolean>,
    msname: Nullable<string>,
    baseids: Nullable<number[]>,
    cntids: Nullable<number[]>,
    maxresults: Nullable<number>,
    max_de: Nullable<number>,
    deoption: Nullable<number>
  ): ApiThunk<MatchingResponse> {
    return authenticated.post<MatchingResponse>(
      `/matching/systemgroup/${systemgroupid}/zone/${zoneid}/match`,
      {
        siteid,
        refl,
        productid,
        cansizeid,
        nominalamount,
        gravimetric,
        msname,
        baseids,
        cntids,
        maxresults,
        max_de,
        deoption,
      }
    );
  }

  static matchingCorrectFormula(
    zoneid: number,
    systemgroupid: number,
    siteid: Nullable<number>,
    productid: number,
    cansizeid: Nullable<number>,
    nominalamount: Nullable<number>,
    gravimetric: Nullable<boolean>,
    formula: MatchFormula,
    stdrefl: Refl_Type,
    batchrefl: Refl_Type,
    msname: Nullable<string>,
    deoption: Nullable<number>
  ): ApiThunk<FormulaCorrectionResponse> {
    return authenticated.post<FormulaCorrectionResponse>(
      `/matching/systemgroup/${systemgroupid}/zone/${zoneid}/correct_formula`,
      {
        siteid,
        productid,
        cansizeid,
        nominalamount,
        gravimetric,
        formula,
        stdrefl,
        batchrefl,
        msname,
        deoption,
      }
    );
  }

  static matchingSynthesizeProperties(
    zoneid: number,
    systemgroupid: number,
    productid: number,
    formula: MatchFormula,
    properties: SynthesizeProperty[]
  ): ApiThunk<SynthesizePropertiesResponse> {
    return authenticated.post<SynthesizePropertiesResponse>(
      `/matching/systemgroup/${systemgroupid}/zone/${zoneid}/synthesize_properties`,
      {
        productid,
        formula,
        properties,
      }
    );
  }

  static fetchColorDetails(
    zoneid: number,
    colourid: number
  ): ApiThunk<ColourDetails> {
    return authenticated.get<ColourDetails>(
      '/query/zone/' + zoneid + '/colour_details/' + colourid
    );
  }

  static searchLocalFormulas(
    zoneid: number,
    siteid: number,
    search?: Nullable<string>
  ): ApiThunk<LocalFormula[]> {
    const params = omitNil({ search });
    return authenticated.get<LocalFormula[]>(
      `/query/zone/${zoneid}/site/${siteid}/local_formulas/`,
      { params }
    );
  }

  static hideLocalFormula(
    siteid: number,
    itemid: number
  ): ApiThunk<{ count: number }> {
    return authenticated.post<{ count: number }>(
      `/query/site/${siteid}/hide_local_formula/item/${itemid}`
    );
  }

  static loadCompatibleLocalFormula(
    zoneid: number,
    itemid: number,
    baseid: number
  ): ApiThunk<CompatibleLocalFormula[]> {
    return authenticated.get<CompatibleLocalFormula[]>(
      `/query/zone/${zoneid}/compatible_local_formula/itemid/${itemid}/baseid/${baseid} `
    );
  }

  static choosePrimer(
    parameters: ChoosePrimerParameters
  ): ApiThunk<PrimerColour | Record<string, never> | null> {
    const { systemgroupid, zoneid, productid, colourid, formula, primer_mode } =
      omitNil(parameters);

    return authenticated.post<PrimerColour | Record<string, never> | null>(
      `/matching/systemgroup/${systemgroupid}/zone/${zoneid}/choose_primer`,
      {
        productid,
        colourid,
        formula,
        primer_mode,
      }
    );
  }

  static formulaChanges(
    parameters: FormulaChangesParameters
  ): ApiThunk<FormulaChange[]> {
    const { systemgroupid, zoneid } = omitNil(parameters);

    return authenticated.get<FormulaChange[]>('/query/formula_changes/', {
      params: { systemgroupid, zoneid },
    });
  }
}
